import { clamp01 } from "../math.js";
import type { Ripple, RippleSource, System, World } from "./types.js";
import { rollChain, compile } from "../effects/face-chain.js";
import { drawGust, gustFrontInto, gustFullRadius, type GustFront } from "./gust.js";

export interface RippleConfig {
  dur: number; // how long a wave lives, in seconds
}
// `dur` is a strength-0 wave's life. A strong flap throws a faster front (`waveDurScale`).
// 1.7s felt too fast.
export const RIPPLE_DEFAULTS: RippleConfig = {
  dur: 2.55,
};

/** Scales a wave's life by its flap strength `s` (0 to 1): a strong flap throws a faster
 *  front. The wing's flap timing uses the same curve. */
export function waveDurScale(s: number): number {
  return 1.0 - 0.45 * s;
}

/** A wave's life, in seconds. Its front ends when it does. */
export function waveLife(baseDur: number, strength: number): number {
  return baseDur * waveDurScale(clamp01(strength));
}

/** Fills `out` with a live wave's front at `now`, centered on the origin
    (`originX`, `originY`). */
export function rippleFrontInto(
  out: GustFront,
  ripple: Ripple,
  now: number,
  baseDur: number,
  originX = 0,
  originY = 0,
): void {
  const u = (now - ripple.t0) / waveLife(baseDur, ripple.strength);
  gustFrontInto(out, ripple.gust, ripple.full, u, originX, originY);
}

/** An ambient flap's effect level, 1 to 5, from its strength. The level decides which effects a
 *  wave can roll. Clicks get their level from how fast you click instead. Ambient beats get it
 *  from how hard they flap, so a soft one is level 1 and a big one reaches the top. */
export function levelFromStrength(s: number): number {
  return s < 0.2 ? 1 : s < 0.4 ? 2 : s < 0.6 ? 3 : s < 0.8 ? 4 : 5;
}

/** The most waves that can be live at once. The face draws every one (`MAX_RIPPLES` in
    render-state.ts). */
export const MAX_LIVE_RIPPLES = 4;

export class RippleSystem implements System {
  constructor(
    private readonly cfg: RippleConfig = RIPPLE_DEFAULTS,
    private readonly rng: () => number = Math.random,
  ) {}

  update(world: World, _dt: number): void {
    const now = world.time;
    const items = world.ripples.items;
    for (let i = items.length - 1; i >= 0; i--) {
      const ripple = items[i]!;
      if (now - ripple.t0 > waveLife(this.cfg.dur, ripple.strength)) items.splice(i, 1);
    }
  }

  /** Up to `MAX_LIVE_RIPPLES` waves at once, so clicks a second apart each get their own. A
      click's wave lives 1.6s to 1.9s, so at one click a second, up to 2 overlap. Every source
      counts toward the same limit, and nothing starts while the host holds waves. */
  canEmit(world: World): boolean {
    if (world.ripples.held?.()) return false;
    return world.ripples.items.length < MAX_LIVE_RIPPLES;
  }

  /** Starts a wave. Returns false when it can't (`canEmit`). `strength` (0 to 1) only sets the
      wave's speed. */
  emit(
    world: World,
    now: number,
    amp: number,
    strength = 1,
    source: RippleSource = "ambient",
    level = 3,
  ): boolean {
    if (!this.canEmit(world)) return false;
    world.ripples.items.push(this.newWave(world, source, now, amp, strength, level));
    return true;
  }

  private newWave(
    world: World,
    source: RippleSource,
    now: number,
    amp: number,
    strength: number,
    level: number,
  ): Ripple {
    // The level decides the face effects (see `levelFromStrength` and the click gesture). A
    // wave at level 3 or more rolls a face chain; a softer one leaves the face dots alone. The
    // gust is drawn after the chain, so a given seed still rolls the same chains it always did.
    const faceOps = level >= 3 ? compile(rollChain(this.rng)) : [];
    const gust = drawGust(this.rng);
    const full = gustFullRadius(gust.driftAngle, world.ripples.exitPoints);
    return { source, t0: now, amp, strength, level, faceOps, gust, full };
  }
}
